import React, {useCallback, useEffect, useState} from 'react';
import {Alert} from 'antd';

const VideoFrame = ({onStopRecording, step, recorder, ...props}) => {

    const maxTime = parseInt(step.target_length_seconds);
    const [currentTime, setCurrentTime] = useState(props.currentTime);
    const [isRecording, setIsRecording] = useState(true);
    const timeRemaining = maxTime - currentTime;
    const [warningState, setWarningState] = useState('warning'); // 'normal', 'warning', 'urgent'

    // Format time as MM:SS
    const formatTime = (seconds) => {
        return `${Math.floor(seconds / 60)
            .toString()
            .padStart(2, '0')}:${Math.floor(seconds % 60).toString().padStart(2, '0')}`;
    };

    // Get status configuration based on warning state
    const getStatusConfig = () => {
        switch (warningState) {
            case 'urgent':
                return {
                    progressStatus: 'exception',
                    progressStrokeColor: TB.config('color.danger'),
                    alertType: 'error',
                    message: `${Math.ceil(timeRemaining)} seconds remaining`
                };
            case 'warning':
                return {
                    progressStatus: 'warning',
                    progressStrokeColor: TB.config('color.warning-darker'),
                    alertType: 'warning',
                    message: `Recording will end soon. ${Math.ceil(timeRemaining)} secs`,
                };
            default:
                return {
                    progressStatus: 'active',
                    progressStrokeColor: '#000',
                    alertType: 'info',
                    message: ''
                };
        }
    };
    // Get styles based on warning state
    const getWarningStyles = () => {
        switch (warningState) {
            case 'urgent':
                return {
                    progressColor: 'bg-red-500',
                    textColor: 'text-red-600',
                    alertBg: 'bg-red-50',
                    alertBorder: 'border-red-200',
                    alertText: 'text-red-700',
                    pulseAnimation: 'animate-pulse'
                };
            case 'warning':
                return {
                    progressColor: 'bg-amber-500',
                    textColor: 'text-amber-600',
                    alertBg: 'bg-amber-50',
                    alertBorder: 'border-amber-200',
                    alertText: 'text-amber-700',
                    pulseAnimation: ''
                };
            default:
                return {
                    progressColor: 'bg-blue-500',
                    textColor: 'text-gray-700',
                    alertBg: '',
                    alertBorder: '',
                    alertText: '',
                    pulseAnimation: ''
                };
        }
    };

    // Update warning state based on time remaining
    useEffect(() => {
        if (timeRemaining <= 5) {
            setWarningState('urgent');
        } else if (timeRemaining <= 10) {
            setWarningState('warning');
        } else {
            setWarningState('normal');
        }
    }, [timeRemaining]);

    // Reset recording
    const resetRecording = useCallback(() => {
        setCurrentTime(0);
        setIsRecording(false);
        setWarningState('normal');
    }, []);

    // Start recording
    const startRecording = useCallback(() => {
        resetRecording();
        setIsRecording(true);
    }, [resetRecording]);

    // Timer effect
    useEffect(() => {
        let animationFrame;
        let startTime;

        const updateTimer = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = timestamp - startTime;
            const newTime = Math.min(maxTime, progress / 1000);

            setCurrentTime(newTime);

            if (newTime < maxTime && isRecording) {
                animationFrame = requestAnimationFrame(updateTimer);
            } else {
                onStopRecording();
                setIsRecording(false);
            }
        };

        if (isRecording) {
            animationFrame = requestAnimationFrame(updateTimer);
        }

        return () => {
            if (animationFrame) {
                cancelAnimationFrame(animationFrame);
            }
        };
    }, [isRecording, maxTime]);

    const statusConfig = getStatusConfig();

    const styles = getWarningStyles();
    return (
        <React.Fragment>
            {/* Progress bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200">
                <div
                    className={`h-full transition-all duration-200 ${styles.progressColor}`}
                    style={{ width: `${(currentTime / maxTime) * 100}%` }}
                />
            </div>

            {/* Warning alert */}
            {warningState !== 'normal' && (
                <div className="absolute top-12 left-1/2 -translate-x-1/2 w-auto min-w-64">
                    <Alert
                        message={statusConfig.message}
                        type={statusConfig.alertType}
                        showIcon
                        className={warningState === 'urgent' ? 'animate-pulse rounded-sm' : ' rounded-sm'}
                    />
                </div>
            )}

            {/* Bottom controls container */}
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                {/* Recording time */}
                <div className={`w-20 mr-auto flex items-center gap-2 bg-white bg-opacity-90 px-3 py-1.5 rounded-full ${
                    warningState === 'urgent' ? 'animate-pulse' : ''
                }`}>
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    <span style={{
                        color: statusConfig.progressStrokeColor,
                        fontWeight: 500
                    }}>
                        {formatTime(currentTime)}
                    </span>
                </div>


                {/* Stop recording button */}
                <div
                    className="mx-auto flex-grow flex flex-col items-center justify-center">
                    <button onClick={() => onStopRecording()}
                            disabled={currentTime < 1}
                            className="bg-white bg-opacity-40 backdrop-blur-sm px-8 py-2.5 rounded-full text-gray-700 font-medium hover:bg-opacity-50 transition-all">
                        Stop recording
                    </button>

                </div>

                {/* Practice button */}
                {/* push left elements */}
                <div className="ml-auto w-20">

                </div>
            </div>
        </React.Fragment>
    );
};

export default VideoFrame;